import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  FormField,
  OTPCodeInput,
  PrimaryButton,
} from "../../UI/DesignStandards";
import { supabase } from "../../../supabaseClient";
import { sanitizeEmail } from "../../../utils/sanitization";

const EntryAccountCard = ({ user, email = "", notice = null }) => {
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [accountEmail, setAccountEmail] = useState(() =>
    sanitizeEmail(email)
  );
  const normalized = sanitizeEmail(accountEmail);

  useEffect(() => {
    const fromBowler = sanitizeEmail(email);
    if (!fromBowler) return;
    setAccountEmail((current) => current || fromBowler);
  }, [email]);

  if (user) {
    return (
      <Card className="mb-8">
        <CardHeader title="Account" />
        <div className="p-6 text-sm text-brand-muted">
          Signed in as {user.email}
        </div>
      </Card>
    );
  }

  const sendCode = async (event) => {
    event.preventDefault();
    if (!normalized) {
      setError("Enter your email.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: normalized,
        options: { shouldCreateUser: true },
      });
      if (otpError) {
        setError("Something went wrong. Please try again.");
        return;
      }
      setCodeSent(true);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (code) => {
    setVerifying(true);
    setError(null);
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: normalized,
        token: code,
        type: "email",
      });
      if (verifyError) {
        setError("This code is incorrect or expired.");
      }
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setLoading(true);
    setError(null);
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: normalized,
        options: { shouldCreateUser: true },
      });
      if (otpError) setError("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader
        title="Account"
        subtitle="Sign in here to add this entry to a squad."
      />
      <div className="p-6">
        {notice ? (
          <p data-signup-error="" className="mb-4 text-sm text-red-600">
            {notice}
          </p>
        ) : null}
        {codeSent ? (
          <OTPCodeInput
            email={normalized}
            onVerify={verify}
            onBack={() => {
              setCodeSent(false);
              setError(null);
            }}
            onResend={resend}
            loading={loading}
            error={error}
            verifying={verifying}
          />
        ) : (
          <form onSubmit={sendCode} className="space-y-4">
            <FormField
              label="Email"
              type="email"
              value={accountEmail}
              onChange={(e) => {
                setAccountEmail(sanitizeEmail(e.target.value));
                if (error) setError(null);
              }}
              placeholder="your.email@example.com"
              required
              autoComplete="email"
              error={error}
            />
            <PrimaryButton type="submit" disabled={loading || !normalized}>
              {loading ? "Sending..." : "Send code"}
            </PrimaryButton>
          </form>
        )}
      </div>
    </Card>
  );
};

export default EntryAccountCard;
